import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowRight, Calendar, Calculator, Trash2, Upload, Eye } from 'lucide-react';
import { addServiceCall, getBranches, getServiceCalls, updateServiceCall, getSuppliers, getPurchaseInvoices, getPurchaseInvoiceDownloadUrl, getNextServiceCallNumber, getPriceItems, addPriceItem } from '../api';
import { toast } from '../hooks/use-toast';
import { FloatingDropdown } from '../components/FloatingDropdown';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { useRecordVat, withVat, useExemptCustomerVat } from '../lib/vat';
import { VatSummary } from '../components/VatSummary';
import { PrintButtons } from '../components/PrintButtons';
import { ConsolidatedPaidBadge } from '../components/ConsolidatedPaidBadge';

const NewServiceCall = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const [isEditMode, setIsEditMode] = useState(false);
  const [branches, setBranches] = useState([]);
  const [formData, setFormData] = useState({
    customerName: '',
    branch: '',
    callDate: '',
    issueDescription: '',
    employeeName: ''
  });
  const [employeeNames, setEmployeeNames] = useState([]);
  const [consolidated, setConsolidated] = useState({ id: '', note: '' });
  const [workItems, setWorkItems] = useState([
    { id: '1', partName: '', quantity: '', costPrice: '', transactionAmount: '', profit: '', hasInvoice: false, invoiceNumber: '', supplier: '' }
  ]);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [callNumber, setCallNumber] = useState(null);

  // Global price list (מחירון כללי) for part name autocomplete
  const GLOBAL_PRICE_LIST_ID = '__global__';
  const [priceItems, setPriceItems] = useState([]);
  const [activePartRow, setActivePartRow] = useState(null);

  // Invoice selection dialog state
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [currentItemId, setCurrentItemId] = useState(null);
  const [purchaseInvoices, setPurchaseInvoices] = useState([]);
  const [searchInvoice, setSearchInvoice] = useState('');

  // Calculator state
  const [showCalculator, setShowCalculator] = useState(false);
  const [calcDisplay, setCalcDisplay] = useState('0');
  const [calcPrevValue, setCalcPrevValue] = useState(null);
  const [calcOperation, setCalcOperation] = useState(null);
  const [calcNewNumber, setCalcNewNumber] = useState(true);

  // Exit confirmation dialog state
  const [isDirty, setIsDirty] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  useEffect(() => {
    loadBranches();
    loadSuppliers();
    loadPurchaseInvoices();
    getPriceItems(GLOBAL_PRICE_LIST_ID).then(data => setPriceItems(data || [])).catch(() => setPriceItems([]));
    getServiceCalls().then(calls => setEmployeeNames([...new Set((calls || []).map(c => (c.employeeName || '').trim()).filter(Boolean))])).catch(() => {});
    if (editId) {
      loadServiceCallData();
    } else {
      getNextServiceCallNumber().then(n => setCallNumber(n));
    }
  }, [editId]);

  // Track form changes and prevent accidental exit
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const loadSuppliers = async () => {
    try {
      const data = await getSuppliers();
      setSuppliers(data);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const loadPurchaseInvoices = async () => {
    try {
      const data = await getPurchaseInvoices();
      setPurchaseInvoices(data);
    } catch (error) {
      console.error('Error loading purchase invoices:', error);
    }
  };

  const vatState = useRecordVat();
  useExemptCustomerVat(vatState, formData.customerName, !editId);
  const callSubtotal = () => workItems.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.transactionAmount) || 0), 0);

  const loadServiceCallData = async () => {
    try {
      const serviceCalls = await getServiceCalls();
      const call = serviceCalls.find(c => c.id === editId);
      if (call) {
        setIsEditMode(true);
        vatState.loadFromRecord(call);
        setCallNumber(call.callNumber ?? null);
        setFormData({
          customerName: call.customerName,
          branch: call.branch,
          callDate: call.callDate || '',
          issueDescription: call.issueDescription,
          employeeName: call.employeeName || ''
        });
        setConsolidated({ id: call.consolidatedInvoiceId || '', note: call.consolidatedPaidNote || '' });
        if (call.workItems && call.workItems.length > 0) {
          setWorkItems(call.workItems.map((item, index) => ({
            ...item,
            id: item.id || String(index + 1)
          })));
        }
      }
    } catch (error) {
      console.error('Error loading service call:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לטעון את הקריאה',
        variant: 'destructive'
      });
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches();
      setBranches(data);
    } catch (error) {
      console.error('Error loading branches:', error);
    }
  };

  const today = new Date().toLocaleDateString('he-IL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Calculator functions
  const handleCalcNumber = (num) => {
    if (calcNewNumber) {
      setCalcDisplay(num.toString());
      setCalcNewNumber(false);
    } else {
      setCalcDisplay(calcDisplay === '0' ? num.toString() : calcDisplay + num);
    }
  };

  const handleCalcOperation = (op) => {
    const current = parseFloat(calcDisplay);
    
    if (calcPrevValue === null) {
      setCalcPrevValue(current);
    } else if (calcOperation) {
      const result = calculateResult();
      setCalcDisplay(result.toString());
      setCalcPrevValue(result);
    }
    
    setCalcNewNumber(true);
    setCalcOperation(op);
  };

  const calculateResult = () => {
    const prev = calcPrevValue;
    const current = parseFloat(calcDisplay);
    
    switch (calcOperation) {
      case '+':
        return prev + current;
      case '-':
        return prev - current;
      case '*':
        return prev * current;
      case '/':
        return prev / current;
      default:
        return current;
    }
  };

  const handleCalcEquals = () => {
    if (calcOperation && calcPrevValue !== null) {
      const result = calculateResult();
      setCalcDisplay(result.toString());
      setCalcPrevValue(null);
      setCalcOperation(null);
      setCalcNewNumber(true);
    }
  };

  const handleCalcClear = () => {
    setCalcDisplay('0');
    setCalcPrevValue(null);
    setCalcOperation(null);
    setCalcNewNumber(true);
  };

  const handleCalcDecimal = () => {
    if (!calcDisplay.includes('.')) {
      setCalcDisplay(calcDisplay + '.');
      setCalcNewNumber(false);
    }
  };

  // Work Items functions
  const selectPriceItem = (itemId, pi) => {
    setIsDirty(true);
    setWorkItems(prev => {
      const updated = prev.map(item => {
        if (item.id !== itemId) return item;
        let quantity = parseFloat(item.quantity) || 0;
        if (!item.quantity || quantity === 0) quantity = 1;
        const transaction = parseFloat(pi.price) || 0;
        const cost = parseFloat(pi.costPrice) || parseFloat(item.costPrice) || 0;
        const profit = ((transaction * quantity) - (quantity * cost)).toFixed(2);
        return {
          ...item,
          partName: pi.partName,
          quantity: String(quantity),
          costPrice: pi.costPrice ? String(pi.costPrice) : item.costPrice,
          transactionAmount: pi.price ? String(pi.price) : item.transactionAmount,
          profit,
          supplier: pi.supplier || item.supplier
        };
      });
      const last = updated[updated.length - 1];
      if ((last.partName || last.quantity || last.costPrice || last.transactionAmount) && updated.length === prev.length) {
        const newId = prev.length > 0 ? Math.max(...prev.map(i => parseInt(i.id))) + 1 : 1;
        return [...updated, { id: String(newId), partName: '', quantity: '', costPrice: '', transactionAmount: '', profit: '', hasInvoice: false, invoiceNumber: '', supplier: '' }];
      }
      return updated;
    });
    setActivePartRow(null);
  };

  const addItemToPriceList = async (item) => {
    if (!item.partName || !item.partName.trim()) return;
    try {
      const added = await addPriceItem(GLOBAL_PRICE_LIST_ID, {
        partName: item.partName.trim(),
        price: item.transactionAmount || '',
        costPrice: item.costPrice || '',
        supplier: item.supplier || '',
        invoiceId: '',
        date: ''
      });
      setPriceItems(prev => [...prev, added]);
      setActivePartRow(null);
      toast({ title: 'נוסף למחירון', description: `"${item.partName.trim()}" נוסף למחירון הכללי` });
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן להוסיף למחירון', variant: 'destructive' });
    }
  };

  const handleItemChange = (id, field, value) => {
    const updatedItems = workItems.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        
        // If hasInvoice is checked, open invoice upload dialog
        if (field === 'hasInvoice' && value === true) {
          setCurrentItemId(id);
          setShowInvoiceDialog(true);
        }
        
        // Auto-calculate profit: רווח = (סכום עסקה × כמות) - (כמות × מחיר עלות)
        if (field === 'quantity' || field === 'transactionAmount' || field === 'costPrice') {
          const quantity = parseFloat(updated.quantity) || 0;
          const transaction = parseFloat(updated.transactionAmount) || 0;
          const cost = parseFloat(updated.costPrice) || 0;
          updated.profit = ((transaction * quantity) - (quantity * cost)).toFixed(2);
        }
        
        return updated;
      }
      return item;
    });
    setWorkItems(updatedItems);
    setIsDirty(true);
    
    // Auto-add new row if last row is being filled
    const lastItem = updatedItems[updatedItems.length - 1];
    const isLastItemFilled = lastItem.partName || lastItem.quantity || lastItem.costPrice || lastItem.transactionAmount;
    
    if (isLastItemFilled && updatedItems.length === workItems.length) {
      const newId = workItems.length > 0 ? Math.max(...workItems.map(i => parseInt(i.id))) + 1 : 1;
      setWorkItems([...updatedItems, { 
        id: String(newId), 
        partName: '', 
        quantity: '', 
        costPrice: '', 
        transactionAmount: '', 
        profit: '',
        hasInvoice: false,
        invoiceNumber: '',
        supplier: ''
      }]);
    }
  };

  const removeWorkItem = (id) => {
    if (workItems.length > 1) {
      setWorkItems(workItems.filter(item => item.id !== id));
    }
  };

  // Invoice selection functions
  const handleSelectInvoice = (invoice) => {
    // Update the work item with selected invoice number
    const updatedItems = workItems.map(item => {
      if (item.id === currentItemId) {
        return {
          ...item,
          invoiceNumber: invoice.invoiceNumber,
          hasInvoice: true
        };
      }
      return item;
    });
    setWorkItems(updatedItems);

    toast({
      title: 'הצלחה',
      description: `חשבונית ${invoice.invoiceNumber} נבחרה`
    });

    // Close dialog and reset
    setShowInvoiceDialog(false);
    setSearchInvoice('');
    setCurrentItemId(null);
  };

  const handleInvoiceDialogClose = () => {
    // Uncheck the checkbox if user closes without selecting
    if (currentItemId) {
      const updatedItems = workItems.map(item => {
        if (item.id === currentItemId) {
          return { ...item, hasInvoice: false };
        }
        return item;
      });
      setWorkItems(updatedItems);
    }
    
    setShowInvoiceDialog(false);
    setSearchInvoice('');
    setCurrentItemId(null);
  };

  const filteredInvoices = purchaseInvoices.filter(invoice =>
    invoice.invoiceNumber.toLowerCase().includes(searchInvoice.toLowerCase()) ||
    (invoice.fileName && invoice.fileName.toLowerCase().includes(searchInvoice.toLowerCase()))
  );

  const handleViewInvoice = async (invoice) => {
    try {
      const downloadUrl = getPurchaseInvoiceDownloadUrl(invoice.id);
      const newWindow = window.open(downloadUrl, '_blank');
      
      if (!newWindow) {
        toast({
          title: 'אזהרה',
          description: 'חסימת חלונות קופצים מופעלת. אנא אפשר חלונות קופצים.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error viewing invoice:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לצפות בחשבונית. הקובץ עלול להיות פגום או לא קיים.',
        variant: 'destructive'
      });
    }
  };

  const handleNavigation = (path) => {
    if (isDirty) {
      setPendingNavigation(path);
      setShowExitDialog(true);
    } else {
      navigate(path);
    }
  };

  const confirmExit = () => {
    setIsDirty(false);
    setShowExitDialog(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  const cancelExit = () => {
    setShowExitDialog(false);
    setPendingNavigation(null);
  };

  const [showEmptyWarning, setShowEmptyWarning] = useState(false);

  const handleSubmit = async (e, skipEmptyCheck = false) => {
    e?.preventDefault?.();
    
    console.log('Form submitted. FormData:', formData);
    console.log('Work items:', workItems);
    
    if (!formData.customerName || !formData.branch) {
      console.log('Validation failed: missing customer or branch');
      toast({
        title: 'שגיאה',
        description: 'נא למלא שם לקוח וסניף',
        variant: 'destructive'
      });
      return;
    }

    // Validate at least one work item with data
    const hasValidItem = workItems.some(item => 
      item.partName.trim() || item.quantity.trim() || item.costPrice.trim() || item.transactionAmount.trim()
    );
    
    console.log('Has valid item:', hasValidItem);
    
    if (!hasValidItem) {
      console.log('Validation failed: no valid items');
      toast({
        title: 'שגיאה',
        description: 'נא למלא לפחות פריט עבודה אחד',
        variant: 'destructive'
      });
      return;
    }

    const totalAmount = workItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.transactionAmount) || 0), 0);
    if (!skipEmptyCheck && totalAmount <= 0) {
      setShowEmptyWarning(true);
      return;
    }

    console.log('Validation passed, starting submit...');
    setIsSubmitting(true);
    try {
      const dataToSubmit = {
        ...formData,
        vatRate: vatState.effectiveRate,
        workItems: workItems
          .filter(item => item.partName.trim() || item.quantity.trim() || item.costPrice.trim() || item.transactionAmount.trim())
          .map(item => ({
            ...item,
            id: String(item.id)
          }))
      };
      
      console.log('=== DATA TO SUBMIT ===');
      console.log('Full data:', JSON.stringify(dataToSubmit, null, 2));
      console.log('Work items count:', dataToSubmit.workItems.length);
      console.log('First item ID type:', typeof dataToSubmit.workItems[0]?.id);
      
      if (isEditMode) {
        console.log('Updating service call:', editId);
        await updateServiceCall(editId, dataToSubmit);
        console.log('Update successful');
        toast({
          title: 'הצלחה!',
          description: 'קריאת השירות עודכנה בהצלחה'
        });
        // After update, go back to home
        setIsDirty(false);
        setTimeout(() => {
          navigate('/');
        }, 500);
      } else {
        console.log('Creating new service call');
        const result = await addServiceCall(dataToSubmit);
        console.log('Create successful:', result);
        toast({
          title: 'הצלחה!',
          description: `קריאת השירות #${result.callNumber} נוספה בהצלחה`
        });
        // After create, reset form for new entry and refresh next number
        setIsDirty(false);
        getNextServiceCallNumber().then(n => setCallNumber(n));
        setFormData({
          customerName: '',
          branch: '',
          callDate: '',
          issueDescription: '',
          employeeName: ''
        });
        setWorkItems([
          { id: '1', partName: '', quantity: '', costPrice: '', transactionAmount: '', profit: '', hasInvoice: false, invoiceNumber: '', supplier: '' }
        ]);
      }
    } catch (error) {
      console.error('Error saving service call:', error);
      toast({
        title: 'שגיאה',
        description: `שגיאה: ${error.message || 'לא ניתן לשמור'}`,
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setIsDirty(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4" dir="rtl">
      <div className="container mx-auto max-w-3xl">
        <Card className="shadow-2xl border-0">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
            <div className="flex justify-between items-center">
              <CardTitle className="text-3xl font-bold flex items-center gap-3" data-testid="service-call-title">
                {isEditMode ? 'מכללה טכנולוגית - עריכת קריאה' : 'מכללה טכנולוגית - קריאה חדשה'}
                {callNumber != null && (
                  <span className="bg-white/25 text-white text-xl px-3 py-1 rounded-full backdrop-blur-sm" data-testid="new-service-call-number">
                    #{callNumber}
                  </span>
                )}
              </CardTitle>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  onClick={() => setShowCalculator(!showCalculator)}
                  variant="outline"
                  className="border-white text-white hover:bg-white/20 flex items-center gap-2"
                >
                  <Calculator className="w-4 h-4" />
                  {showCalculator ? 'סגור מחשבון' : 'מחשבון'}
                </Button>
                <div className="flex items-center gap-2 text-sm bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm">
                  <Calendar className="w-4 h-4" />
                  <span>{today}</span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-8">
            {/* Calculator */}
            {showCalculator && (
              <div className="mb-6 bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-xl shadow-2xl">
                <div className="max-w-sm mx-auto">
                  <div className="bg-green-400 text-right text-black p-4 rounded-lg mb-4 text-3xl font-bold min-h-[60px] flex items-center justify-end break-all">
                    {calcDisplay}
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {/* Row 1 */}
                    <button type="button" onClick={handleCalcClear} className="col-span-2 bg-red-500 hover:bg-red-600 text-white font-bold py-4 rounded-lg transition-all">C</button>
                    <button type="button" onClick={() => handleCalcOperation('/')} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-lg transition-all">÷</button>
                    <button type="button" onClick={() => handleCalcOperation('*')} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-lg transition-all">×</button>

                    {/* Row 2 */}
                    <button type="button" onClick={() => handleCalcNumber(7)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg transition-all">7</button>
                    <button type="button" onClick={() => handleCalcNumber(8)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg transition-all">8</button>
                    <button type="button" onClick={() => handleCalcNumber(9)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg transition-all">9</button>
                    <button type="button" onClick={() => handleCalcOperation('-')} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-lg transition-all">-</button>

                    {/* Row 3 */}
                    <button type="button" onClick={() => handleCalcNumber(4)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg transition-all">4</button>
                    <button type="button" onClick={() => handleCalcNumber(5)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg transition-all">5</button>
                    <button type="button" onClick={() => handleCalcNumber(6)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg transition-all">6</button>
                    <button type="button" onClick={() => handleCalcOperation('+')} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-lg transition-all">+</button>

                    {/* Row 4 */}
                    <button type="button" onClick={() => handleCalcNumber(1)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg transition-all">1</button>
                    <button type="button" onClick={() => handleCalcNumber(2)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg transition-all">2</button>
                    <button type="button" onClick={() => handleCalcNumber(3)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg transition-all">3</button>
                    <button type="button" onClick={handleCalcEquals} className="row-span-2 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg transition-all">=</button>

                    {/* Row 5 */}
                    <button type="button" onClick={() => handleCalcNumber(0)} className="col-span-2 bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg transition-all">0</button>
                    <button type="button" onClick={handleCalcDecimal} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg transition-all">.</button>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-8">
              <ConsolidatedPaidBadge invoiceId={consolidated.id} note={consolidated.note} testId="servicecall-consolidated-paid" />
              {/* Customer Name */}
              <div className="space-y-3">
                <Label htmlFor="customerName" className="text-lg font-semibold text-gray-700">
                  איש קשר <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="customerName"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleChange}
                  placeholder="הזן שם איש הקשר"
                  className="text-lg py-6 border-2 focus:border-indigo-500 transition-colors"
                  required
                />
              </div>

              {/* Branch */}
              <div className="space-y-3">
                <Label htmlFor="branch" className="text-lg font-semibold text-gray-700">
                  סניף <span className="text-red-500">*</span>
                </Label>
                <select
                  id="branch"
                  name="branch"
                  value={formData.branch}
                  onChange={handleChange}
                  className="w-full text-lg py-6 px-3 border-2 rounded-md focus:border-indigo-500 transition-colors bg-white"
                  required
                >
                  <option value="">בחר סניף...</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.name}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                {branches.length === 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    אין סניפים במערכת. הוסף סניפים דרך עמוד המנהל.
                  </p>
                )}
              </div>

              {/* Call Date */}
              <div className="space-y-3">
                <Label htmlFor="callDate" className="text-lg font-semibold text-gray-700">
                  תאריך קריאה
                </Label>
                <Input
                  id="callDate"
                  name="callDate"
                  type="date"
                  value={formData.callDate}
                  onChange={handleChange}
                  className="text-lg py-6"
                />
              </div>

              {/* Issue Description */}
              <div className="space-y-3">
                <Label htmlFor="issueDescription" className="text-lg font-semibold text-gray-700">
                  תיאור תקלה
                </Label>
                <Textarea
                  id="issueDescription"
                  name="issueDescription"
                  value={formData.issueDescription}
                  onChange={handleChange}
                  placeholder="תאר את התקלה בפירוט..."
                  className="text-lg min-h-[80px] border-2 focus:border-indigo-500 transition-colors resize-y"
                />
              </div>

              {/* Work Items Table */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Label className="text-lg font-bold text-gray-800">פירוט עבודה</Label>
                </div>
                
                <div className="overflow-x-auto border-2 border-gray-200 rounded-lg min-h-[400px]">
                  <table className="w-full border-collapse bg-white">
                    <thead>
                      <tr className="bg-gradient-to-r from-indigo-500 to-blue-500 text-white">
                        <th className="border border-gray-300 p-3 text-center w-[18%]">שם חלק/עבודה</th>
                        <th className="border border-gray-300 p-3 text-center w-[7%]">יש חשבונית</th>
                        <th className="border border-gray-300 p-3 text-center w-[10%]">מספר חשבונית</th>
                        <th className="border border-gray-300 p-3 text-center w-[8%]">כמות</th>
                        <th className="border border-gray-300 p-3 text-center w-[10%]">מחיר עלות</th>
                        <th className="border border-gray-300 p-3 text-center w-[10%]">מחיר ללקוח</th>
                        <th className="border border-gray-300 p-3 text-center w-[14%]">רווח</th>
                        <th className="border border-gray-300 p-3 text-center w-[12%]">ספק חלק</th>
                        <th className="border border-gray-300 p-3 text-center w-[6%]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {workItems.map((item) => (
                        <tr key={item.id} className="hover:bg-blue-50 transition-colors">
                          <td className="border border-gray-300 p-2 relative">
                            <Textarea
                              value={item.partName}
                              onChange={(e) => {
                                handleItemChange(item.id, 'partName', e.target.value);
                                setActivePartRow(item.id);
                              }}
                              onFocus={() => setActivePartRow(item.id)}
                              onBlur={() => setTimeout(() => setActivePartRow(null), 200)}
                              placeholder="תאר את החלק/העבודה"
                              className="border-0 focus:ring-2 focus:ring-indigo-500 resize-none min-h-[60px]"
                              autoComplete="off"
                              data-testid={`servicecall-partname-${item.id}`}
                            />
                            {activePartRow === item.id && (() => {
                              const q = (item.partName || '').toLowerCase();
                              const matches = (q ? priceItems.filter(pi => (pi.partName || '').toLowerCase().includes(q)) : priceItems).slice(0, 50);
                              const hasText = !!(item.partName && item.partName.trim());
                              const exists = hasText && priceItems.some(pi => (pi.partName || '').trim().toLowerCase() === item.partName.trim().toLowerCase());
                              if (matches.length === 0 && !hasText) return null;
                              return (
                                <FloatingDropdown className="bg-white border-2 border-indigo-300 rounded-lg shadow-xl" data-testid={`servicecall-price-suggestions-${item.id}`}>
                                  {matches.map((pi) => (
                                    <button
                                      key={pi.id}
                                      type="button"
                                      className="w-full text-right px-4 py-2 hover:bg-indigo-50 transition-colors flex items-center justify-between border-b border-gray-100 last:border-0"
                                      onMouseDown={(e) => { e.preventDefault(); selectPriceItem(item.id, pi); }}
                                    >
                                      <span className="text-sm font-medium text-gray-800">{pi.partName}</span>
                                      {pi.price && <span className="text-sm font-bold text-indigo-700">₪{pi.price}</span>}
                                    </button>
                                  ))}
                                  {hasText && !exists && (
                                    <button
                                      type="button"
                                      className="w-full text-right px-4 py-2.5 bg-green-50 hover:bg-green-100 transition-colors flex items-center gap-2 text-green-800 font-semibold border-t border-green-200"
                                      onMouseDown={(e) => { e.preventDefault(); addItemToPriceList(item); }}
                                      data-testid={`servicecall-add-to-pricelist-${item.id}`}
                                    >
                                      <span className="text-lg leading-none">＋</span>
                                      <span className="text-sm">הוסף "{item.partName.trim()}" למחירון</span>
                                    </button>
                                  )}
                                </FloatingDropdown>
                              );
                            })()}
                          </td>
                          <td className="border border-gray-300 p-2 text-center">
                            <Checkbox
                              checked={item.hasInvoice || false}
                              onCheckedChange={(checked) => handleItemChange(item.id, 'hasInvoice', checked)}
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              value={item.invoiceNumber || ''}
                              onChange={(e) => handleItemChange(item.id, 'invoiceNumber', e.target.value)}
                              placeholder="מספר חשבונית"
                              className="border-0 focus:ring-2 focus:ring-indigo-500 text-center"
                              disabled={!item.hasInvoice}
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)}
                              placeholder="0"
                              className="border-0 focus:ring-2 focus:ring-indigo-500 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.costPrice}
                              onChange={(e) => handleItemChange(item.id, 'costPrice', e.target.value)}
                              placeholder="0.00"
                              className="border-0 focus:ring-2 focus:ring-orange-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.transactionAmount}
                              onChange={(e) => handleItemChange(item.id, 'transactionAmount', e.target.value)}
                              placeholder="0.00"
                              className="border-0 focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.profit}
                              readOnly
                              className="border-0 bg-green-50 font-semibold text-base [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <select
                              value={item.supplier || ''}
                              onChange={(e) => handleItemChange(item.id, 'supplier', e.target.value)}
                              className="w-full p-2 border-0 focus:ring-2 focus:ring-indigo-500 rounded"
                            >
                              <option value="">בחר ספק...</option>
                              {suppliers.map((supplier) => (
                                <option key={supplier.id} value={supplier.name}>
                                  {supplier.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="border border-gray-300 p-2 text-center">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeWorkItem(item.id)}
                              disabled={workItems.length === 1}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex flex-wrap items-center gap-3 p-4 bg-indigo-50 border-2 border-indigo-100 rounded-lg" data-testid="servicecall-employee-row">
                  <Label htmlFor="employeeName" className="text-lg font-semibold text-gray-700 whitespace-nowrap">שם העובד</Label>
                  <Input
                    id="employeeName"
                    name="employeeName"
                    list="servicecall-employee-options"
                    value={formData.employeeName}
                    onChange={handleChange}
                    placeholder="מי ביצע את העבודה?"
                    className="max-w-sm text-lg border-2 focus:border-indigo-500 bg-white"
                    data-testid="servicecall-employee-input"
                  />
                  <datalist id="servicecall-employee-options">
                    {employeeNames.map(n => <option key={n} value={n} />)}
                  </datalist>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
                <PrintButtons testIdPrefix="servicecall" docRef={editId ? { type: 'service_call', recordId: editId } : null} getDoc={() => ({
                  title: `קריאת שירות${callNumber ? ` #${callNumber}` : ''}`,
                  customerName: formData.customerName,
                  date: formData.callDate,
                  extraInfo: [['סניף', formData.branch], ['תיאור', formData.issueDescription], ...(formData.employeeName ? [['שם העובד', formData.employeeName]] : [])],
                  items: workItems.filter(i => i.partName || i.transactionAmount).map(i => ({ name: i.partName, quantity: i.quantity, price: i.transactionAmount, cost: i.costPrice })),
                  vatRate: vatState.effectiveRate
                })} />
                <VatSummary subtotal={callSubtotal()} rate={vatState.effectiveRate} noVat={vatState.noVat} onNoVatChange={vatState.setNoVat} testIdPrefix="servicecall-vat" />
              </div>
              <div className="flex gap-4 pt-6">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-6 text-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {isSubmitting ? 'שומר...' : 'שמור קריאה'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleNavigation('/')}
                  className="px-8 py-6 text-lg border-2 hover:bg-gray-50 transition-colors"
                >
                  <ArrowRight className="ml-2 w-5 h-5" />
                  חזור
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Invoice Selection Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={(open) => !open && handleInvoiceDialogClose()}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">בחירת חשבונית קנייה</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 p-4">
            {/* Search */}
            <div className="space-y-2">
              <Label className="text-lg font-semibold">חיפוש חשבונית</Label>
              <Input
                value={searchInvoice}
                onChange={(e) => setSearchInvoice(e.target.value)}
                placeholder="חפש לפי מספר חשבונית או שם קובץ..."
                className="text-lg"
              />
            </div>

            {/* Invoices List */}
            <div className="space-y-2">
              <Label className="text-lg font-semibold">
                חשבוניות זמינות ({filteredInvoices.length})
              </Label>
              
              {filteredInvoices.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-lg">לא נמצאו חשבוניות</p>
                  <p className="text-sm mt-2">נא להעלות חשבוניות דרך תפריט "חשבוניות קנייה"</p>
                </div>
              ) : (
                <div className="border-2 border-gray-200 rounded-lg max-h-[400px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white sticky top-0">
                      <tr>
                        <th className="p-3 text-right">מספר חשבונית</th>
                        <th className="p-3 text-right">תאריך</th>
                        <th className="p-3 text-right">שם קובץ</th>
                        <th className="p-3 text-center">פעולות</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInvoices.map((invoice) => (
                        <tr 
                          key={invoice.id} 
                          className="border-b hover:bg-blue-50 transition-colors"
                        >
                          <td className="p-3 font-semibold text-blue-700">
                            {invoice.invoiceNumber}
                          </td>
                          <td className="p-3">
                            {invoice.invoiceDate}
                          </td>
                          <td className="p-3 text-sm text-gray-600">
                            {invoice.fileName}
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex gap-2 justify-center">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewInvoice(invoice)}
                                className="border-blue-500 text-blue-600 hover:bg-blue-50"
                              >
                                <Eye className="ml-1 w-4 h-4" />
                                צפייה
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleSelectInvoice(invoice)}
                                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                              >
                                בחר
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleInvoiceDialogClose}
                className="text-lg px-6"
              >
                ביטול
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exit Confirmation Dialog */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">
              האם לצאת בלי לשמור?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 p-4">
            <p className="text-center text-gray-600 text-lg">
              יש שינויים שלא נשמרו. האם אתה בטוח שברצונך לצאת?
            </p>
            <div className="flex gap-3 justify-center pt-4">
              <Button
                onClick={cancelExit}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-6 text-lg"
              >
                המשך לערוך
              </Button>
              <Button
                onClick={confirmExit}
                variant="outline"
                className="border-2 border-red-500 text-red-600 hover:bg-red-50 px-8 py-6 text-lg"
              >
                צא בלי לשמור
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showEmptyWarning} onOpenChange={setShowEmptyWarning}>
        <AlertDialogContent dir="rtl" data-testid="empty-call-warning">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-right">הקריאה בלי סכום</AlertDialogTitle>
            <AlertDialogDescription className="text-right">
              לא הוזן סכום עסקה (כמות × סכום) באף פריט. קריאה בלי סכום תופיע בדוח המכללה עם ₪0. לשמור בכל זאת?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2 flex-row-reverse sm:justify-start">
            <AlertDialogCancel data-testid="empty-call-back-btn">חזור למילוי</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setShowEmptyWarning(false); handleSubmit(null, true); }}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="empty-call-save-anyway-btn"
            >
              שמור בכל זאת
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default NewServiceCall;// Force rebuild Sat Nov 22 17:52:11 UTC 2025
